import {
  Column,
  Loading,
  Panel,
  Table,
  Tooltip,
} from "@appkit4/react-components";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Packer } from "docx";
import { saveAs } from "file-saver";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CompanyPanel from "../../components/CompanyPanel.js";
import toast from "../../components/DismissibleToast.js";
import { BackButton, NextButton } from "../../components/ProgressNav.js";
import {
  Button,
  CalendarPicker,
  Checkbox,
  Input,
} from "../../components/ReadonlyAwareInputs";
import CustomTableHeader from "../../components/tableHeader/tableHeader.js";
import { AuthContext } from "../../services/AuthProvider.js";
import { addCharge } from "../../services/Charging.service.js";
import {
  draftReportingCompanyExists,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getGroupName,
  getPageTooltips,
  getPeriodByIndex,
  getPoaIndex,
  getRepCo,
  getSelectedPeriod,
  setGroupData,
} from "../../services/GroupContext";
import sqldatabaseService from "../../services/sqldatabase/sqldatabase.service.js";
import { processAbbreviatedReturn } from "../../utils/AbbReturnOutput";
import { formatAbbreviatedReturn } from "../../utils/AbbrReturnFormatting";
import { processDateJS } from "../../utils/dateProcessor";
import {
  defaultReturnStatus,
  validReturnTypes,
} from "../GroupPOA/GroupPoAUtils.js";
import { getGroupElectionsTableData } from "./../reporting-company/ReportingCompany.js";
import ConfirmationModal from "./ConfirmationModal.js";
import { downloadExcel } from "./excel/utils/ExcelOutput.js";

import { downloadFullReturn } from "./excel/utils/FullReturnOutput.js";
import ReturnModal from "./ReturnModal.js";
import { createWordReport } from "./word/utils/wordExport.js";

dayjs.extend(isSameOrBefore);

const ExcelJS = require("exceljs");

const submissionReferenceFormat =
  /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/;

const optionDisabledNote = (
  <span className="error">
    This option is disabled. Hover over the button to view the necessary
    conditions to enable it.
  </span>
);

const Outputs = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const selectedPeriod = getSelectedPeriod() ?? {};
  const { hasPoaSpecificWbsCode, wbsCode: poaWbsCode } = selectedPeriod ?? {};

  const savedData = getGroupData();
  const poaIndex = getPoaIndex();
  const auth = useContext(AuthContext);
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const tenantId = useSelector((state) => state.usageInfo.tenantID);
  const tooltips = useMemo(() => getPageTooltips("outputs"), []);
  const groupName = useMemo(() => getGroupName(), []);

  const [readyToFinalise, setReadyToFinalise] = useState(false);
  const [returnStatus, setReturnStatus] = useState(defaultReturnStatus);
  const [isReturnTypeValid, setIsReturnTypeValid] = useState(false);
  const [isRevisionValid, setIsRevisionValid] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  const [wbsCode, setWbsCode] = useState("");
  const [wbsCodeError, setWbsCodeError] = useState(false);

  const [returnsEnabled, setReturnsEnabled] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [openModalReturnType, setOpenModalReturnType] = useState(null);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const hasActiveCompany = Boolean(selectedPeriod?.reporting_company ?? false);
  const shouldZeroRemainingAllocation =
    selectedPeriod?.shouldZeroRemainingAllocation ?? false;
  const fullReturnDisabled =
    wbsCodeError ||
    !hasActiveCompany ||
    shouldZeroRemainingAllocation ||
    (selectedPeriod?.apply_NARRC2 ?? false);
  const bfUnusedAllowancesUtilised =
    (selectedPeriod?.group_bf_interest_allowance ?? 0) >
    Math.max(
      (selectedPeriod?.interest_capacity_de_minimis ?? 0) -
        (selectedPeriod?.group_interest_allowance ?? 0),
      0,
    );
  const abbreviatedReturnDisabled =
    wbsCodeError ||
    !hasActiveCompany ||
    (selectedPeriod?.group_reactivations ?? 0) !== 0 ||
    (selectedPeriod?.group_disallowance ?? 0) !== 0 ||
    bfUnusedAllowancesUtilised;
  const previousPoA = getPeriodByIndex(poaIndex - 1);
  const isPrevPeriodUnFinalised =
    previousPoA && !previousPoA?.return_status?.isFinalised;

  const [submissionReference, setSubmissionReference] = useState(
    selectedPeriod?.return_status?.submission_reference ?? "",
  );
  const [isSubmissionDateInvalid, setIsSubmissionDateInvalid] = useState(true);
  const isSubmissionReferenceVisible =
    returnStatus?.type === "Full" || returnStatus?.type === "Abbreviated";
  const navigate = useNavigate();

  useEffect(() => {
    const saved_data = getGroupData();
    if (!saved_data || poaIndex === -1) {
      return;
    }

    setWbsCode(
      (hasPoaSpecificWbsCode ? poaWbsCode : saved_data?.wbsCode) ?? "",
    );

    const { return_status } = savedData?.periods_of_account[poaIndex] ?? {};
    if (return_status) {
      setReturnStatus(return_status);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const newWbsCodeError =
      !isClientFacing && !String(wbsCode).match(/^\d{11}/);
    setWbsCodeError(newWbsCodeError);
    setReturnsEnabled(
      !newWbsCodeError &&
        !returnStatus?.isSubmitted &&
        !isPrevPeriodUnFinalised,
    );
  }, [wbsCode, returnStatus]);

  useEffect(() => {
    if (!returnStatus) return;

    setIsReturnTypeValid(
      Object.values(validReturnTypes).includes(returnStatus.type),
    );

    setIsRevisionValid(
      returnStatus?.isRevised
        ? returnStatus?.nature_of_revision?.length > 0
        : true,
    );
  }, [returnStatus]);

  const fullElectionsTableData =
    getGroupElectionsTableData(savedData, selectedPeriod) ?? {};
  const electionsTableData = [
    "group-ratio",
    "group-ratio-blended",
    "group-ebitda",
    "interest-alt-calc",
    "interest-non-con-investment",
    "interest-con-partnership",
  ].map((id) => fullElectionsTableData?.[id] ?? {});

  const isElectionThisYear = (table, property) =>
    table?.[property]?.status &&
    processDateJS(table?.[property]?.dateMade)?.isSame(
      processDateJS(selectedPeriod?.period_end),
    );

  const shouldDisableReturnWithoutReportingCompany = [
    fullElectionsTableData["group-ratio"]?.status,
    fullElectionsTableData["group-ratio-blended"]?.status,
    isElectionThisYear(fullElectionsTableData, "interest-alt-calc"),
    fullElectionsTableData["interest-non-con-investment"]?.status,
    isElectionThisYear(fullElectionsTableData, "interest-con-partnership"),
    selectedPeriod?.adjusted_caps?.some((cap) => !cap.non_consenting_company),
  ].some((v) => v === true);
  const noReportingCompanyReturnDisabled =
    hasActiveCompany ||
    shouldDisableReturnWithoutReportingCompany ||
    (selectedPeriod?.group_reactivations ?? 0) !== 0 ||
    bfUnusedAllowancesUtilised;
  const RepCO = getRepCo() ?? "";
  const getRepCoStatus = () => {
    if (selectedPeriod?.reporting_company) return "Reporting Company Appointed";
    else if (draftReportingCompanyExists()) return "Reporting Company Drafted";
    else return RepCO;
  };

  const getConsentingCompanies = () => {
    return [
      ...new Set(
        selectedPeriod?.adjusted_caps
          ?.filter((cap) => !cap.non_consenting_company)
          ?.map((cap) => cap.company_name) ?? [],
      ),
    ];
  };

  // create file in browser
  const jsonFileName =
    groupName +
    " - Summary output - " +
    processDateJS(selectedPeriod?.period_start)?.format("DD/MM/YYYY") +
    " - " +
    processDateJS(selectedPeriod?.period_end)?.format("DD/MM/YYYY");
  const blob = new Blob([JSON.stringify(savedData, null, 2)], {
    type: "application/json",
  });

  function downloadJson() {
    saveAs(blob, jsonFileName + ".json");
  }

  async function donwloadAbbreviatedReturn() {
    const workbook = new ExcelJS.Workbook();
    const group_period_sheet = workbook?.addWorksheet(
      "(1)_Group,_period,_return_type",
    );
    const group_level_elections_sheet = workbook?.addWorksheet(
      "(2)_Group_level_elections",
    );
    const uk_group_companies_sheet = workbook?.addWorksheet(
      "(3)_UK_group_companies_",
    );

    const groupData = getGroupData();
    const full_return_data = processAbbreviatedReturn(
      groupData,
      poaIndex,
      selectedPeriod,
    );
    const group_period_return_data = full_return_data[0];
    group_period_sheet?.addRows(group_period_return_data);

    const group_level_elections_data = full_return_data[1];
    group_level_elections_sheet?.addRows(group_level_elections_data);

    const uk_group_companies_data = full_return_data[2];
    uk_group_companies_sheet?.addRows(uk_group_companies_data);

    formatAbbreviatedReturn(
      group_period_sheet,
      group_level_elections_sheet,
      uk_group_companies_sheet,
    );

    const period_start = processDateJS(selectedPeriod?.period_start)?.format(
      "DD/MM/YYYY",
    );
    const period_end = processDateJS(selectedPeriod?.period_end)?.format(
      "DD/MM/YYYY",
    );
    const fullReportFilename =
      groupName +
      " - Abbreviated return draft - " +
      period_start +
      " - " +
      period_end;
    const excelFileName = fullReportFilename + ".xlsx";
    const buffer = await workbook.xlsx.writeBuffer();
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const blob = new Blob([buffer], { type: fileType });
    saveAs(blob, excelFileName);
  }

  async function downloadWordReport() {
    const doc = await createWordReport(isClientFacing);
    const period_start = processDateJS(selectedPeriod?.period_start)?.format(
      "DD/MM/YYYY",
    );
    const period_end = processDateJS(selectedPeriod?.period_end)?.format(
      "DD/MM/YYYY",
    );
    const fullReportFilename =
      groupName + " - CIR Return Output - " + period_start + " - " + period_end;
    const wordFileName = fullReportFilename + ".docx";

    try {
      const blob = await Packer.toBlob(doc);
      saveAs(blob, wordFileName);
    } catch (error) {
      console.error("Error generating document", error);
    }
  }

  const getFailedConditionsText = (returnType) => {
    const commonMessages = {
      noValidWBS: "No valid WBS code has been provided.",
      isPrevPeriodUnFinalised:
        "There are prior periods in the app which have not been finalised.",
      returnStatusSubmitted:
        "A return has already been submitted for this period, please re-open the period if the return needs to be revised.",
      noReportingCompany: "No active reporting company has been recorded.",
      bfUnusedAllowancesUtilised:
        "Utilisation of brought forward unused allowances has been recorded.",
    };
    const messages = [];
    if (returnType === "modelling") {
      if (wbsCodeError) {
        messages.push(commonMessages.noValidWBS);
      }
    } else if (returnType === "full") {
      if (isPrevPeriodUnFinalised) {
        messages.push(commonMessages.isPrevPeriodUnFinalised);
      }
      if (returnStatus?.isSubmitted) {
        messages.push(commonMessages.returnStatusSubmitted);
      }
      if (wbsCodeError) {
        messages.push(commonMessages.noValidWBS);
      }
      if (!hasActiveCompany) {
        messages.push(commonMessages.noReportingCompany);
      }
      if (shouldZeroRemainingAllocation) {
        messages.push(
          "Reactivations have been disabled in the 'Reactivation of interest' page. Please re-enable these to generate a full return.",
        );
      }
      if (selectedPeriod?.apply_NARRC2) {
        messages.push(
          "Utilisation of brought forward unused allowances has been restricted on the Review Calculations - Fixed/Group Ratio Election page. Please re-enable these to generate a full return.",
        );
      }
    } else if (returnType === "abbreviated") {
      if (isPrevPeriodUnFinalised) {
        messages.push(commonMessages.isPrevPeriodUnFinalised);
      }
      if (returnStatus?.isSubmitted) {
        messages.push(commonMessages.returnStatusSubmitted);
      }
      if (wbsCodeError) {
        messages.push(commonMessages.noValidWBS);
      }
      if (!hasActiveCompany) {
        messages.push(commonMessages.noReportingCompany);
      }
      if ((selectedPeriod?.group_reactivations ?? 0) !== 0) {
        messages.push(
          "An abbreviated return cannot be made because reactivations have been made in the period.",
        );
      }
      if ((selectedPeriod?.group_disallowance ?? 0) !== 0) {
        messages.push(
          "An abbreviated return cannot be made because a disallowance was calculated for the period.",
        );
      }
      if (bfUnusedAllowancesUtilised) {
        messages.push(commonMessages.bfUnusedAllowancesUtilised);
      }
    } else if (returnType === "noReportingCompany") {
      const elections = electionsTableData
        .filter(
          (election) =>
            election?.status &&
            processDateJS(election?.dateMade)?.isSame(
              processDateJS(selectedPeriod?.period_end),
            ),
        )
        .map((data) => data.election)
        .join(", ");
      const consentingCompanies = getConsentingCompanies()?.join(", ");

      if (isPrevPeriodUnFinalised) {
        messages.push(commonMessages.isPrevPeriodUnFinalised);
      }
      if (returnStatus?.isSubmitted) {
        messages.push(commonMessages.returnStatusSubmitted);
      }
      if (wbsCodeError) {
        messages.push(commonMessages.noValidWBS);
      }
      if (hasActiveCompany) {
        messages.push("An active reporting company has been recorded.");
      }
      if ((selectedPeriod?.group_reactivations ?? 0) !== 0) {
        messages.push("Reactivations have been made in the period.");
      }
      if (bfUnusedAllowancesUtilised) {
        messages.push(commonMessages.bfUnusedAllowancesUtilised);
      }
      if (shouldDisableReturnWithoutReportingCompany) {
        if (elections) {
          messages.push(`The following elections have been made: ${elections}`);
        }
        if (consentingCompanies) {
          messages.push(
            `The following companies are treated as consenting: ${consentingCompanies}`,
          );
        }
      }
    }

    return (
      <div className="no-margin-last">
        {messages.map((msg, index) => (
          <p key={index}>{msg}</p>
        ))}
      </div>
    );
  };

  const downloadDocsByReturnType = () => {
    downloadJson();
    downloadExcel(isClientFacing);
    // Temporarily disable Word export in UAT
    // [TODO] Re-enable once Word export is no longer required to be disabled in UAT
    // downloadWordReport();

    if (openModalReturnType === "full") {
      downloadFullReturn();
    } else if (openModalReturnType === "abbreviated") {
      donwloadAbbreviatedReturn();
    }
  };

  const handleSubmissionReferenceInput = (value) => {
    const nonAlphaNumeric = /[^a-zA-Z0-9-]/g;
    if (value.length > 14 || value.match(nonAlphaNumeric)) return;

    const withoutDashText = value.replaceAll("-", "");
    const segment1 = withoutDashText.substring(0, 4);
    const segment2 = withoutDashText.substring(4, 8);
    const segment3 = withoutDashText.substring(8, 12);

    let fullText = segment1;
    if (segment2) fullText += "-" + segment2;
    if (segment3) fullText += "-" + segment3;
    if (value.at(-1) === "-" && withoutDashText.length % 4 === 0)
      fullText += "-";

    setSubmissionReference(fullText);
  };

  const save = async (returnStatus, selectedReturnType) => {
    if (auth?.isReadOnlyUser() || getCurrentPeriodIsReadonly(true)) {
      return;
    }

    const savedData = getGroupData();

    if (poaIndex === -1) throw `Error: failed to find period of account`;

    setIsPosting(true);

    if ((wbsCode.length === 0 || !wbsCodeError) && !isClientFacing) {
      if (hasPoaSpecificWbsCode) {
        savedData.periods_of_account[poaIndex].wbsCode = wbsCode;
      } else {
        savedData.wbsCode = wbsCode;
      }
    }

    savedData.periods_of_account[poaIndex].return_status = returnStatus;

    // Toggle electionSubmitted for consolidated partnerships when return is submitted
    if (returnStatus.isSubmitted && savedData.consolidated_partnerships) {
      const periodEnd = processDateJS(selectedPeriod?.period_end);
      savedData.consolidated_partnerships =
        savedData.consolidated_partnerships.map((partnership) => {
          const partnershipDate = processDateJS(partnership.date);
          // Set electionSubmitted to true if partnership date is on or before PoA end date
          if (
            partnershipDate &&
            periodEnd &&
            partnershipDate.isSameOrBefore(periodEnd, "day")
          ) {
            return { ...partnership, electionSubmitted: true };
          }
          return partnership;
        });
    }

    let chargePromise = null;
    const shouldCharge = Boolean(selectedReturnType);
    if (shouldCharge && !isClientFacing) {
      chargePromise = addCharge(
        tenantId,
        "fixed",
        null,
        wbsCode,
        selectedPeriod,
        isClientFacing,
        selectedReturnType,
      );
    }

    const savePromise = sqldatabaseService?.postResponse(savedData);
    const onSuccess = () => {
      setGroupData(savedData);
      setReturnStatus(returnStatus);
      setIsPosting(false);
    };
    const onError = () => {
      setIsPosting(false);
    };
    const combinedPromise = (
      chargePromise ? chargePromise.then(() => savePromise) : savePromise
    ).catch((err) => {
      console.error("Error during charging or saving", err);
      throw "Error during charging or saving";
    });
    return toast.save(combinedPromise, onSuccess, onError);
  };

  const submitPeriod = (props = {}) => {
    save({
      ...returnStatus,
      submission_reference: submissionReference,
      ...props,
    });
  };

  if (isLoading)
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  return (
    <div>
      {/* Group Info */}
      <div className="ap-container mt-4">
        <CompanyPanel showStatus={true} returnStatus={returnStatus} />
      </div>

      <div className="ap-container">
        <Panel title="Reporting Company Status" className="mt-2">
          <div className="ap-container mt-4">
            <table className="table" style={{ width: "100%" }}>
              <thead>
                <tr style={{ textAlign: "left" }}>
                  <CustomTableHeader
                    title="Reporting Company Status"
                    required={false}
                    tooltipText={tooltips.reportingCompanyStatus}
                  />
                  <CustomTableHeader
                    title="Reporting Company Name"
                    required={false}
                    tooltipText={tooltips.reportingCompanyName}
                  />
                  <CustomTableHeader
                    title="View Reporting Company Status"
                    required={false}
                    tooltipText={tooltips.viewReportingCompanyStatus}
                  />
                </tr>
              </thead>
              <tbody>
                <td>{getRepCoStatus()}</td>
                <td>{RepCO}</td>
                <td style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    enabledWhenFinalised
                    onClick={() => navigate("/app/reportingcompany")}
                  >
                    Go to Reporting Company
                  </Button>
                </td>
              </tbody>
            </table>
          </div>

          {!hasActiveCompany && (
            <>
              <p className="bold no-margin mt-4">
                Elections which require an active reporting company
              </p>
              <p>
                The following elections cannot be made in the return if there is
                no active reporting company, however any irrevocable election
                made in a previous CIR period should remain active as they
                cannot be revoked:
              </p>
              <Table
                originalData={electionsTableData}
                hasTitle
                disableDefaultSort
                skipInitialSort
              >
                <Column field="election">Group Elections</Column>
                <Column field="statReference">Statutory Reference </Column>
                <Column field="duration">Duration</Column>
                <Column
                  field="status"
                  renderCell={(row, field) =>
                    row[field] === true ? (
                      <span style={{ color: "#ff6666" }}>Active</span>
                    ) : (
                      "Inactive"
                    )
                  }
                >
                  Status
                </Column>
                <Column
                  field="dateMade"
                  renderCell={(row, field) =>
                    row[field] ? processDateJS(row[field])?.year() : ""
                  }
                >
                  Year in which election was made (if provided)
                </Column>
              </Table>
            </>
          )}

          {!hasActiveCompany && (
            <div className="mt-4" style={{ paddingLeft: "12px" }}>
              <p className="bold no-margin">Consenting Companies</p>
              <p>
                If there is no active reporting company, all companies need to
                be treated as non-consenting. The following companies are
                currently classed as consenting:
              </p>
              {getConsentingCompanies().length > 0 ? (
                <ul>
                  {getConsentingCompanies().map((companyName) => (
                    <li>{companyName}</li>
                  ))}
                </ul>
              ) : (
                <span>None</span>
              )}
            </div>
          )}
        </Panel>
        {!isClientFacing && (
          <Panel title="WBS entry" className="mt-2">
            <p>
              <span>
                To prepare and file a CIR return, a usage charge will be applied
                - please provide the market team WBS code below. It will not be
                possible to prepare and file the CIR return or obtain access to
                any other outputs until this is provided. For further
                information, please refer to the{" "}
              </span>
              <a
                className="ap-link"
                href="https://pwcgbr.sharepoint.com/:w:/r/teams/GB-CoreBuild-CIRAppDevelopment/Shared Documents/CIRA Usage Charges.docx?d=we201144891a645c294540b7a145f9ca6&csf=1&web=1&e=bvb8kj"
                target="_blank"
                rel="noreferrer"
              >
                guidance linked here.
              </a>
            </p>
            <label className="col-sm-2 col-form-label align-self-center">
              WBS code:
            </label>
            <Input
              title="WBS Code"
              className="col-2"
              value={wbsCode}
              enabledWhenFinalised
              error={wbsCodeError}
              onChange={(value) => {
                value = String(value).trim().slice(0, 11);

                setWbsCode(value);
              }}
            />
          </Panel>
        )}
        <Panel title="Generate Outputs" className="mt-2">
          {/* <p>The submission of an original full return incurs a recharge based on the number of UK group companies, as per the table below, in addition to any fees that may have been chargeable for One Source extractions.<br />The submission of a revised full return incurs a fixed recharge of £500.<br />Abbreviated returns, whether original or revised, incur a fixed recharge of £250.<br/>The generation of modelling outputs incurs a £1,000 fixed charge.</p>
                    <Table originalData={chargeTableData} hasTitle disableDefaultSort skipInitialSort >
                        <Column field='0'>Number of UK group companies</Column>
                        <Column field='1'>Fixed recharge</Column>
                    </Table> */}

          <ReturnModal
            visible={isReturnModalOpen}
            type={openModalReturnType}
            onCancel={() => setIsReturnModalOpen(false)}
            returnStatus={returnStatus}
            save={save}
            isLoading={isPosting}
            download={downloadDocsByReturnType}
          />

          <ConfirmationModal
            visible={isConfirmModalVisible}
            isLoading={isPosting}
            onCancel={() => setIsConfirmModalVisible(false)}
            onClick={async () => {
              await save(
                {
                  ...returnStatus,
                  isRevisionCharged: returnStatus?.isRevised,
                  type: "No reporting company",
                  isFinalised: true,
                  withoutReportingCompany: true,
                },
                "noReportingCompany",
              )
                .then(() => {
                  downloadJson();
                  downloadExcel(isClientFacing);
                  // Temporarily disable Word export in UAT
                  // [TODO] Re-enable once Word export is no longer required to be disabled in UAT
                  // downloadWordReport();
                })
                .catch((err) => {
                  console.error("Error during saving or downloading", err);
                  toast.error(
                    "An error occurred while saving the return status or downloading the outputs. Please try again.",
                  );
                });

              setIsConfirmModalVisible(false);
            }}
          />

          <div className="row space-between">
            <div className="col-8">
              <p className="mb-0">
                Selecting this option generates standard reports, detailed excel
                outputs and visualisation inputs.
              </p>
              {wbsCodeError && optionDisabledNote}
            </div>
            <Tooltip
              disabled={!wbsCodeError}
              position="top"
              style={{ maxWidth: "50rem" }}
              content={() => getFailedConditionsText("modelling")}
            >
              <div className="col-4">
                <Button
                  style={{ width: "100%" }}
                  enabledWhenFinalised
                  disabled={wbsCodeError}
                  onClick={async () => {
                    try {
                      await save(returnStatus, "modelling")
                        .then(() => {
                          downloadJson();
                          downloadExcel(isClientFacing);
                          // Temporarily disable Word export in UAT
                          // [TODO] Re-enable once Word export is no longer required to be disabled in UAT
                          // downloadWordReport();
                        })
                        .catch((err) => {
                          console.error(
                            "Error during saving or downloading",
                            err,
                          );
                          toast.error(
                            "An error occurred while saving the return status or downloading the outputs. Please try again.",
                          );
                        });
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                  icon="icon-download-light-outline"
                >
                  Preview/modelling outputs
                </Button>
              </div>
            </Tooltip>
          </div>
          <div className="row space-between">
            <div className="col-8">
              <p className="mb-0">
                Selecting this option generates full return templates, in
                addition to standard reports, detailed excel outputs and
                visualisation inputs. This option is only available if there is
                an active reporting company.
              </p>
              {(!returnsEnabled || fullReturnDisabled) && optionDisabledNote}
            </div>
            <Tooltip
              disabled={returnsEnabled && !fullReturnDisabled}
              position="top"
              style={{ maxWidth: "50rem" }}
              content={() => getFailedConditionsText("full")}
            >
              <div className="col-4">
                <Button
                  style={{ width: "100%" }}
                  enabledWhenFinalised
                  onClick={async () => {
                    setIsReturnModalOpen(true);
                    setOpenModalReturnType("full");
                  }}
                  disabled={!returnsEnabled || fullReturnDisabled}
                  icon="icon-download-light-outline"
                >
                  Generate full return
                </Button>
              </div>
            </Tooltip>
          </div>
          <div className="row space-between">
            <div className="col-8">
              <p className="mb-0">
                Selecting this option generates abbreviated return templates, in
                addition to standard reports, detailed excel outputs and
                visualisation inputs. This option is only available if there is
                an active reporting company, and if there were no disallowances
                in the period.
              </p>
              {(!returnsEnabled || abbreviatedReturnDisabled) &&
                optionDisabledNote}
            </div>
            <Tooltip
              disabled={returnsEnabled && !abbreviatedReturnDisabled}
              position="top"
              style={{ maxWidth: "50rem" }}
              content={() => getFailedConditionsText("abbreviated")}
            >
              <div className="col-4">
                <Button
                  style={{ width: "100%" }}
                  enabledWhenFinalised
                  onClick={async () => {
                    setIsReturnModalOpen(true);
                    setOpenModalReturnType("abbreviated");
                  }}
                  disabled={!returnsEnabled || abbreviatedReturnDisabled}
                  icon="icon-download-light-outline"
                >
                  Generate abbreviated return
                </Button>
              </div>
            </Tooltip>
          </div>
          <div className="row space-between">
            <div className="col-8">
              <p className="mb-0">
                Selecting this option generates standard reports, detailed excel
                outputs and visualisation inputs. This option is only available
                if all companies are non-consenting, and none of the elections
                below have been made. This is the only option available if there
                is no active reporting company in the period.
              </p>
              {(!returnsEnabled || noReportingCompanyReturnDisabled) &&
                optionDisabledNote}
            </div>
            <Tooltip
              disabled={returnsEnabled && !noReportingCompanyReturnDisabled}
              position="top"
              style={{ maxWidth: "50rem" }}
              content={() => getFailedConditionsText("noReportingCompany")}
            >
              <div className="col-4">
                <Button
                  style={{ width: "100%" }}
                  enabledWhenFinalised
                  onClick={() => setIsConfirmModalVisible(true)}
                  disabled={!returnsEnabled || noReportingCompanyReturnDisabled}
                  icon="icon-download-light-outline"
                >
                  Complete calculations without reporting company
                </Button>
              </div>
            </Tooltip>
          </div>
        </Panel>

        {!isClientFacing && (
          <Panel title="Generate Word Report" className="mt-2">
            <div className="row space-between">
              <div className="col-8">
                <p className="mb-0">
                  {`A compliance report is being developed to provide to clients to
                support and document the CIR calculations. We would like your
                help in testing this before it is released. Please click the
                button opposite and we will get in touch with you to generate a
                test report for the ${groupName} group.`}
                </p>
              </div>
              <div className="col-4">
                <Button
                  style={{ width: "100%" }}
                  enabledWhenFinalised
                  onClick={() => {
                    const recipients =
                      "john.x.webb@pwc.com,iain.mcdonald@pwc.com,maisy.a.wise@pwc.com,cameron.johnstone@pwc.com";
                    const subject = `Generate Word Report for ${groupName} Group`;
                    const body = `Please can you generate a report for the ${groupName} group.`;
                    const mailtoLink = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    window.open(mailtoLink, "_blank");
                  }}
                  icon="icon-email-outline"
                >
                  Request test report
                </Button>
                {auth?.isAdmin() && (
                  <Button
                    style={{ width: "100%", marginTop: "0.5rem" }}
                    enabledWhenFinalised
                    onClick={downloadWordReport}
                    icon="icon-download-light-outline"
                  >
                    Admin button
                  </Button>
                )}
              </div>
            </div>
          </Panel>
        )}

        <Panel className="mt-2 pt">
          {hasActiveCompany && (
            <>
              <p className="section-header">Review Return</p>
              {returnStatus?.isFinalised && (
                <div className="d-flex gap-2">
                  <p className="d-flex gap-1">
                    <span className="bold">Unique Code:</span>
                    <span>{returnStatus?.code}</span>
                  </p>
                  <p className="d-flex gap-1">
                    <span className="bold">Timestamp:</span>
                    <span>{returnStatus?.timestamp}</span>
                  </p>
                </div>
              )}
              <label className="col-sm-2 col-form-label align-self-center">
                Reviewed by:
              </label>
              <Input
                value={returnStatus?.reviewedBy || ""}
                onChange={(value) =>
                  setReturnStatus({
                    ...returnStatus,
                    reviewedBy: value.trimStart(),
                  })
                }
                error={
                  returnStatus?.isFinalised &&
                  returnStatus?.reviewedBy?.length === 0
                }
                disabled={!returnStatus?.isFinalised}
                enabledWhenFinalised
              />
              <div className="d-flex flex-row justify-content">
                <Checkbox
                  enabledWhenFinalised
                  disabled={!returnStatus?.isFinalised}
                  checked={returnStatus?.reviewConfirmed ?? false}
                  onChange={(value) => {
                    setReturnStatus({
                      ...returnStatus,
                      reviewConfirmed: value,
                      reviewedBy: returnStatus?.reviewedBy?.trimEnd(),
                    });
                  }}
                />
                <p>
                  Please confirm that the CIR calculation/return has been
                  appropriately reviewed.
                </p>
              </div>
            </>
          )}

          <p className="section-header">Submit Return</p>
          <div className="d-flex gap-2">
            <p className="d-flex gap-1">
              <span className="bold">Return type:</span>
              <span>{returnStatus?.type}</span>
            </p>
            <p className="d-flex gap-1">
              <span className="bold">Return status:</span>
              <span>
                {returnStatus?.isSubmitted ? "Submitted" : "Not submitted"}
              </span>
            </p>
          </div>

          <div className="d-flex gap-2">
            {isSubmissionReferenceVisible && (
              <div className="col-sm-2">
                <label>Submission reference:</label>
                <Input
                  enabledWhenFinalised
                  value={submissionReference}
                  error={!submissionReference.match(submissionReferenceFormat)}
                  disabled={returnStatus?.isSubmitted}
                  onChange={handleSubmissionReferenceInput}
                />
              </div>
            )}
            <div className="col-sm-2">
              <label>Date of submission:</label>
              <CalendarPicker
                value={processDateJS(returnStatus?.submittedDate)}
                format="DD/MM/YYYY"
                placeholder="dd/mm/yyyy"
                minDate={processDateJS(selectedPeriod?.period_end)?.add(
                  1,
                  "day",
                )}
                error={returnStatus?.isFinalised && isSubmissionDateInvalid}
                useCustomValidation
                customErrorNode="Please enter a date that is not equal to or before the PoA end"
                enabledWhenFinalised
                disabled={!returnStatus?.isFinalised}
                onChange={(date) => {
                  setReturnStatus({
                    ...returnStatus,
                    submittedDate: processDateJS(date)?.toString(),
                  });
                  setIsSubmissionDateInvalid(
                    !date ||
                      processDateJS(date)?.isSameOrBefore(
                        selectedPeriod?.period_end,
                      ),
                  );
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-sm-4 col-form-label d-flex">
              <label>
                {returnStatus?.withoutReportingCompany
                  ? "I confirm that relevant computations have been submitted."
                  : "I confirm the return has been submitted."}
              </label>
              <Checkbox
                enabledWhenFinalised
                disabled={
                  !isReturnTypeValid ||
                  !isRevisionValid ||
                  returnStatus?.isSubmitted ||
                  !returnStatus?.isFinalised
                }
                checked={readyToSubmit || returnStatus?.isSubmitted}
                onChange={setReadyToSubmit}
              />
            </div>
          </div>

          <div className="flex-end">
            <Button
              className="col-sm-3"
              enabledWhenFinalised
              disabled={
                !readyToSubmit ||
                !isReturnTypeValid ||
                !isRevisionValid ||
                returnStatus?.isSubmitted ||
                !processDateJS(returnStatus?.submittedDate) ||
                (isSubmissionReferenceVisible &&
                  (!submissionReference ||
                    !submissionReference.match(submissionReferenceFormat))) ||
                (hasActiveCompany && !returnStatus?.reviewedBy) ||
                !returnStatus?.reviewConfirmed ||
                isSubmissionDateInvalid
              }
              checked={returnStatus.isSubmitted}
              loading={readyToSubmit && isPosting}
              onClick={() => {
                submitPeriod({ isSubmitted: true });
              }}
            >
              {returnStatus.isSubmitted
                ? "Submitted"
                : hasActiveCompany
                  ? "Mark period submitted"
                  : "Complete calculations without reporting company"}
            </Button>
          </div>
        </Panel>
      </div>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <BackButton disabled={isPosting} />
          </div>
          <div className="d-flex gap-2">
            <Button
              enabledWhenFinalised
              hiddenInReadonly
              loading={isPosting}
              onClick={() => submitPeriod()}
              kind="secondary"
            >
              Save
            </Button>
            <NextButton
              preNavigation={() => save(returnStatus)}
              loading={isPosting}
              nextPage="/app/grouppoa"
            >
              Return to Group POA
            </NextButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Outputs;
